<template>
    <e-drawer ref="suggestionsDrawer" :snap-points="['70px']"
        :sidebar="isDesktopDevice" no-share-button no-close-button sticky-header
        top-mobile-margin="79px" top-sidebar-margin="79px" show-nav
    >
        <template #above>
            <div class="d-flex flex-column align-items-center ml-auto">
                <b-btn variant="white" @click="$emit('reset-camera')" class="pill-btn-xl">
                    <i :class="[fontAwesomePrefix, 'fa-crosshairs']" />
                </b-btn>
            </div>
        </template>

        <template #header>
            <div class="tabs">
                <b-btn v-for="tab in internalTabs" :key="tab.id" size="sm" pill class="tab-btn" :variant="currentTab === tab.id ? 'accent' : 'light'"
                       @click="selectTab(tab.id)" >
                    {{ tab.label }}
                </b-btn>
            </div>
        </template>

        <div class="suggestions">
            <div v-if="currentItemsByGroups.length" class="suggestions-scroll">
                <div class="suggestions-group" v-for="(group, index) in currentItemsByGroups" :key="`g-${index}`">
                    <div class="suggestions-group-label" @click="showSuggestions">{{tr(group.label)}}</div>
                    <button v-for="item in group.items" :key="`s-${item.id}`"
                        class="suggestion-card" type="button"
                        @click="selectItem(item)"
                    >
                        <div class="suggestion-icon">
                            <i :class="item.getGlyph(fontAwesomePrefix)" />
                        </div>
                        <div class="suggestion-text">
                            <div class="suggestion-name text-ellipsis">{{ item.getFullName() }}</div>
                            <div v-if="item.cmsItem?.getSecondaryValue()" class="suggestion-sub text-ellipsis">{{ tr(item.cmsItem.getSecondaryValue()) }}</div>
                        </div>
                    </button>
                </div>
            </div>
            <div v-else class="empty text-muted">
                {{ $t('map.noSuggestions') }}
            </div>
        </div>
    </e-drawer>
</template>

<script>
import EDrawer from '../../e-drawer.vue';
import MapMixin from "@/helpers/maps/map-mixin.js";

export default {
    name: `map-mobile-suggestions`,
    components: { EDrawer },
    mixins: [MapMixin],
    props: {
        tabs: { type: Array, default: null }
    },
    data() {
        return {
            internalTab: null,
        };
    },
    computed: {
        internalTabs() {
            return [
                { id: `popular`, label: this.$t(`map.suggestions.popular`) },
                { id: `departments`, label: this.$t(`map.suggestions.departments`) },
                { id: `services`, label: this.$t(`map.suggestions.services`) },
            ];
        },
        currentTab() {
            return this.internalTab || this.internalTabs[0]?.id;
        },
        currentMapItems() {
            if (!this.currentMap?.items)
                return [];

            let items = [];

            switch (this.currentTab) {
                case `popular`:
                    items = this.currentMap.items?.filter(i => i.cmsItem?.favorite === true);
                    break;
                case `departments`:
                    items = this.currentMap.items.filter((item) => `department|location`.includes(item.cmsItem?.type));
                    break;
                case `services`:
                    items = this.currentMap.items.filter((item) => `service|amenity`.includes(item.cmsItem?.type));
                    break;
            }
            items.sort((a, b) => a.getFullName() - b.getFullName());

            return items;
        },
        currentItemsByGroups() {
            let groups = [];
            const floorItems = this.currentMapItems.filter(i => i.isOnFloor(this.currentFloor?.id));
            if (floorItems.length > 0) {
                groups.push({
                    label: this.$t(`map.suggestions.atThisFloor`),
                    items: floorItems
                })
            }
            const otherFloorItems = this.currentMapItems.filter(i => !i.isOnFloor(this.currentFloor?.id));
            if (otherFloorItems.length > 0) {
                groups.push({
                    label: this.$t(`map.suggestions.atOtherFloors`),
                    items: otherFloorItems
                })
            }
            return groups;
        }
    },
    methods: {
        selectTab(tabId) {
            this.internalTab = tabId;
            this.$emit(`tab-changed`, tabId);
            this.showSuggestions();
        },
        showSuggestions() {
            const drawer = this.$refs.suggestionsDrawer;
            if (!drawer) return;
            drawer.snapToMax();
        },
        selectItem(item) {
            this.$emit(`endpoint-selected`, {
                target: `end`,
                type: `cms-item`,
                cmsItemId: item?.cmsItem?.id || null
            });

            this.$emit(`item-selected`, item);
        }
    },
};
</script>

<style lang="scss" scoped>

.tabs {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;

    .tab-btn {
        flex: 1;
        padding: 8px 16px;
        border: 1px solid rgba(0, 0, 0, 0.06);
    }
}

.suggestions {
    .suggestions-scroll {
        display: flex;
        flex-direction: column;
        gap: 10px;
        overflow: visible;
        padding-bottom: 12px;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .suggestions-group {
        display: flex;
        gap: 10px;
        flex-direction: column;
    }

    .suggestions-group-label {
        font-size: 10pt;
        padding: 5px 10px;
        background: rgba(0, 95, 229, 0.12);
        border-radius: 14px;
        //border-block: 1px solid rgba(black, 0.15);
    }

    .suggestion-card {
        display: flex;
        gap: 10px;
        align-items: center;
        padding: 10px 12px;
        border-radius: 14px;
        background: var(--color-lightest);
        text-align: left;

        border: 1px solid oklch(92.8% .006 264.531);
        --tw-shadow: 0 1px 2px 0 #0000000d;
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, var(--tw-shadow);

        transition: transform 150ms ease, box-shadow 150ms ease;

        &:active,
        &:hover {
            transform: translateY(-1px);
            --tw-shadow: 0 1px 2px 0 #0000001a, 0 2px 4px -1px #0000001a;
        }

        .suggestion-icon {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            background: rgba(0, 95, 229, 0.12);
            color: var(--color-accent);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
        }

        .suggestion-text {
            flex: 1;

            .suggestion-name {
                font-size: 10pt;
            }

            .suggestion-sub {
                font-size: 11pt;
                color: var(--color-muted);
            }
        }
    }

    .empty {
        padding: 8px 4px;
        font-size: 11pt;
    }
}
</style>
